import * as Headless from "@headlessui/react";
import React from "react";
import clsx from "clsx";

/**
 * Shared card styles for filter tiles. The active state is driven by the
 * headlessui `data-checked` attribute so both the radio and checkbox variants
 * can reuse them.
 */
const tileClasses = [
    "group rounded-card flex cursor-pointer items-center gap-3 p-3 text-left transition-colors",
    // Inactive card
    "border border-zinc-950/5 bg-(--surface-card) data-hover:border-zinc-950/10 data-hover:bg-(--surface-card-hovered) dark:border-white/10 dark:data-hover:border-white/20",
    // Active card
    "data-checked:border-(--color-brand-500) data-checked:bg-(--color-brand-50) data-checked:data-hover:bg-(--color-brand-100) dark:data-checked:border-(--color-brand-400)/40 dark:data-checked:bg-(--color-brand-900)/40 dark:data-checked:data-hover:bg-(--color-brand-900)/60",
    // Focus ring
    "focus:outline-hidden data-focus:outline data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-blue-500",
];

/**
 * The tile's content: icon square, small label and large tabular count.
 */
function TileContent({ icon, label, count }: Pick<FilterTileProps<unknown>, "icon" | "label" | "count">) {
    return (
        <>
            <div
                className={clsx(
                    "inline-flex size-9 shrink-0 items-center justify-center rounded-(--radius-control)",
                    "bg-(--color-brand-50) text-(--color-brand-600) dark:bg-(--color-brand-900)/40 dark:text-(--color-brand-300)",
                    "group-data-checked:bg-(--color-brand-600) group-data-checked:text-white dark:group-data-checked:bg-(--color-brand-600) dark:group-data-checked:text-white",
                )}
            >
                {icon}
            </div>
            <div className={"min-w-0 flex-1"}>
                <div
                    className={clsx(
                        "truncate text-xs/4",
                        "text-zinc-500 dark:text-zinc-400",
                        "group-data-checked:text-(--color-brand-700) dark:group-data-checked:text-(--color-brand-200)",
                    )}
                >
                    {label}
                </div>
                <div className={"text-lg font-semibold text-zinc-950 tabular-nums dark:text-white"}>{count}</div>
            </div>
        </>
    );
}

/**
 * The properties for {@link FilterTileGroup}
 */
export type FilterTileGroupProps<T> = {
    /** The currently selected filter value. */
    value: T;
    /** Called with the newly selected value. */
    onChange: (value: T) => void;
    /** Layout classes for the group (e.g. a grid). */
    className?: string;
    /** The {@link FilterTile}s of this group. */
    children: React.ReactNode;
    /** Accessible label for the radio group. */
    "aria-label"?: string;
};

/**
 * Groups mutually exclusive {@link FilterTile}s. Built on
 * `Headless.RadioGroup` so it exposes `role="radiogroup"` and arrow-key
 * navigation between the tiles for accessibility.
 */
export function FilterTileGroup<T>({ value, onChange, className, children, ...props }: FilterTileGroupProps<T>) {
    return (
        <Headless.RadioGroup value={value} onChange={onChange} className={className} {...props}>
            {children}
        </Headless.RadioGroup>
    );
}

/**
 * The properties for {@link FilterTile}
 */
export type FilterTileProps<T> = {
    /** The filter value this tile selects. */
    value: T;
    /** Leading icon (heroicon component or any svg). Rendered in a 36px square. */
    icon: React.ReactNode;
    /** Short label describing the filter value. */
    label: string;
    /** Number of items matching this filter value. */
    count: number;
};

/**
 * A filter option rendered as a stat-tile-style card (icon square, small
 * label, large tabular count) so it can sit in a stat strip. Must be placed
 * inside a {@link FilterTileGroup}; built on `Headless.Radio` so it exposes
 * `role="radio"` and `aria-checked`.
 */
export function FilterTile<T>({ value, icon, label, count }: FilterTileProps<T>) {
    return (
        <Headless.Radio value={value} className={clsx(tileClasses)}>
            <TileContent icon={icon} label={label} count={count} />
        </Headless.Radio>
    );
}

/**
 * The properties for {@link CheckboxFilterTile}
 */
export type CheckboxFilterTileProps = {
    /** Whether this tile's filter is currently enabled. */
    checked: boolean;
    /** Called with the new checked state when the tile is toggled. */
    onChange: (checked: boolean) => void;
    /** Leading icon (heroicon component or any svg). Rendered in a 36px square. */
    icon: React.ReactNode;
    /** Short label describing the filter. */
    label: string;
    /** Number of items matching this filter. */
    count: number;
};

/**
 * A {@link FilterTile}-styled toggle with checkbox semantics: multiple tiles
 * can be enabled independently. Built on `Headless.Checkbox` so it exposes
 * `role="checkbox"`, `aria-checked` and space-key toggling for accessibility.
 */
export function CheckboxFilterTile({ checked, onChange, icon, label, count }: CheckboxFilterTileProps) {
    return (
        <Headless.Checkbox checked={checked} onChange={onChange} className={clsx(tileClasses)}>
            <TileContent icon={icon} label={label} count={count} />
        </Headless.Checkbox>
    );
}
